import { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Award, ImageIcon, AlertCircle, ArrowUp, ArrowDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { honorService, operationLogService } from '@/services/storage';
import type { Honor } from '@/types';
import { toast } from 'sonner';

// 图片上传配置 - 无大小限制
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];

export default function AdminHonors() {
  const [honors, setHonors] = useState<Honor[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingHonor, setEditingHonor] = useState<Honor | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    image: '',
    year: new Date().getFullYear().toString(),
    sortOrder: 0,
  });
  const [errorDialog, setErrorDialog] = useState<{open: boolean; title: string; message: string}>({
    open: false,
    title: '',
    message: ''
  });

  useEffect(() => {
    loadHonors();
  }, []);

  const loadHonors = async () => {
    const data = await honorService.getAll();
    setHonors(data);
  };

  const showError = (title: string, message: string) => {
    setErrorDialog({ open: true, title, message });
  };

  const validateImage = (file: File): boolean => {
    if (!ALLOWED_TYPES.includes(file.type)) {
      showError(
        '文件类型错误',
        `请上传图片文件（支持 ${ALLOWED_TYPES.map(t => t.replace('image/', '').toUpperCase()).join('、')} 格式）`
      );
      return false;
    }
    // 无文件大小限制
    return true;
  };

  const readImageFile = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onerror = () => {
        reject(new Error('图片读取失败'));
      };

      reader.onloadend = () => {
        const result = reader.result as string;
        if (!result || result.length < 100) {
          reject(new Error('图片处理异常'));
          return;
        }
        resolve(result);
      };

      try {
        reader.readAsDataURL(file);
      } catch (error) {
        reject(new Error('无法读取该图片文件'));
      }
    });
  };

  const handleAdd = () => {
    setEditingHonor(null);
    setFormData({
      title: '',
      image: '',
      year: new Date().getFullYear().toString(),
      sortOrder: honors.length,
    });
    setDialogOpen(true);
  };

  const handleEdit = (honor: Honor) => {
    setEditingHonor(honor);
    setFormData({
      title: honor.title,
      image: honor.image || '',
      year: honor.year || new Date().getFullYear().toString(),
      sortOrder: honor.sortOrder || 0,
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (confirm('确定要删除此荣誉资质吗？')) {
      await honorService.delete(id);
      await operationLogService.add({
        user: 'admin',
        action: '删除荣誉资质',
        module: '荣誉资质',
        details: `删除了荣誉资质`,
      });
      toast.success('荣誉资质已删除');
      loadHonors();
    }
  };

  const handleMoveUp = async (index: number) => {
    if (index === 0) return;
    const newHonors = [...honors];
    [newHonors[index - 1], newHonors[index]] = [newHonors[index], newHonors[index - 1]];
    // 更新排序值
    for (let i = 0; i < newHonors.length; i++) {
      await honorService.update(newHonors[i].id, { sortOrder: i });
    }
    await operationLogService.add({
      user: 'admin',
      action: '调整排序',
      module: '荣誉资质',
      details: '上移荣誉资质',
    });
    toast.success('已上移');
    loadHonors();
  };

  const handleMoveDown = async (index: number) => {
    if (index === honors.length - 1) return;
    const newHonors = [...honors];
    [newHonors[index], newHonors[index + 1]] = [newHonors[index + 1], newHonors[index]];
    // 更新排序值
    for (let i = 0; i < newHonors.length; i++) {
      await honorService.update(newHonors[i].id, { sortOrder: i });
    }
    await operationLogService.add({
      user: 'admin',
      action: '调整排序',
      module: '荣誉资质',
      details: '下移荣誉资质',
    });
    toast.success('已下移');
    loadHonors();
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!validateImage(file)) return;

    try {
      const result = await readImageFile(file);
      setFormData({ ...formData, image: result });
      toast.success('图片上传成功');
    } catch (error) {
      showError('上传失败', error instanceof Error ? error.message : '图片上传失败，请重试');
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      showError('缺少标题', '请填写荣誉资质标题');
      return;
    }

    if (editingHonor) {
      honorService.update(editingHonor.id, formData);
      operationLogService.add({
        user: 'admin',
        action: '更新荣誉资质',
        module: '荣誉资质',
        details: `更新了荣誉资质: ${formData.title}`,
      });
      toast.success('荣誉资质已更新');
    } else {
      const newHonor: Honor = {
        id: Date.now().toString(),
        title: formData.title,
        image: formData.image,
        year: formData.year,
        sortOrder: formData.sortOrder,
      };
      honorService.add(newHonor);
      operationLogService.add({
        user: 'admin',
        action: '添加荣誉资质',
        module: '荣誉资质',
        details: `添加了荣誉资质: ${formData.title}`,
      });
      toast.success('荣誉资质已添加');
    }

    setDialogOpen(false);
    loadHonors();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-bold">荣誉资质管理</h1>
        <Button onClick={handleAdd}>
          <Plus className="mr-2 h-5 w-5" />
          添加荣誉资质
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>图片</TableHead>
                <TableHead>标题</TableHead>
                <TableHead>年份</TableHead>
                <TableHead>排序</TableHead>
                <TableHead className="text-right">操作</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {honors.map((honor, index) => (
                <TableRow key={honor.id}>
                  <TableCell>
                    {honor.image ? (
                      <img
                        src={honor.image}
                        alt={honor.title}
                        className="w-16 h-16 object-cover rounded"
                      />
                    ) : (
                      <div className="w-16 h-16 bg-gray-200 rounded flex items-center justify-center">
                        <Award className="h-8 w-8 text-gray-400" />
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="font-medium">{honor.title}</div>
                  </TableCell>
                  <TableCell>{honor.year}</TableCell>
                  <TableCell>{honor.sortOrder}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end space-x-1">
                      <Button variant="ghost" size="sm" onClick={() => handleMoveUp(index)} disabled={index === 0}>
                        <ArrowUp className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleMoveDown(index)} disabled={index === honors.length - 1}>
                        <ArrowDown className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(honor)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(honor.id)}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {honors.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              暂无荣誉资质数据
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingHonor ? '编辑荣誉资质' : '添加荣誉资质'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="title">
                标题 <span className="text-red-500">*</span>
              </Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="请输入荣誉资质标题"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="year">年份</Label>
                <Input
                  id="year"
                  value={formData.year}
                  onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                  placeholder="例如：2024"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="image">
                <span className="flex items-center gap-2">
                  <ImageIcon className="h-4 w-4" />
                  证书/资质图片
                </span>
              </Label>
              <div className="mt-2">
                {formData.image && (
                  <img
                    src={formData.image}
                    alt="Honor"
                    className="w-full h-48 object-contain bg-gray-100 rounded mb-2"
                  />
                )}
                <Input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                />
                <p className="text-xs text-gray-500 mt-1">支持 JPG、PNG、GIF、WebP 格式，最大 5MB</p>
              </div>
            </div>
            <div>
              <Label htmlFor="sortOrder">排序</Label>
              <Input
                id="sortOrder"
                type="number"
                value={formData.sortOrder}
                onChange={(e) => setFormData({ ...formData, sortOrder: parseInt(e.target.value) || 0 })}
                placeholder="数字越小排序越靠前"
              />
              <p className="text-xs text-gray-500 mt-1">数字越小排序越靠前</p>
            </div>
            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                取消
              </Button>
              <Button type="submit">
                <Award className="mr-2 h-4 w-4" />
                保存
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Error Dialog */}
      <Dialog open={errorDialog.open} onOpenChange={(open) => setErrorDialog({ ...errorDialog, open })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="h-5 w-5" />
              {errorDialog.title}
            </DialogTitle>
            <DialogDescription>
              {errorDialog.message}
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end">
            <Button onClick={() => setErrorDialog({ ...errorDialog, open: false })}>
              知道了
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
